import React, { useState, useRef, useEffect } from 'react';
import { getVideoDuration, requestCameraPermission, stopMediaStream, isMobileDevice, checkAudioPresence } from '../../utils/video.js';
import { uploadToGCS, checkResumableUpload, clearSavedUploadState } from '../../utils/storage.js';
import { testimonyApi } from '../../services/api.js';
import { config } from '../../config/config.js';
import { parseErrorMessage } from '../../utils/errors.js';
import styles from '../Common/Common.module.css';
import recordStyles from './RecordStep.module.css';
import resumeStyles from './ResumeDialog.module.css';
import LoadingSpinner from '../Loading/LoadingSpinner.jsx';
import CameraLoading from '../Loading/CameraLoading.jsx';
import UploadProgress from '../Loading/UploadProgress.jsx';
import PageTitle from '../Common/PageTitle.jsx';

const RecordStep = ({ 
  userToken, 
  formData, 
  currentStage = null, // For multi-stage: { stage, stageName, stageTitle }
  allStages = [],
  currentStageIndex = 0,
  completedStages = [],
  progress = 0,
  onUploadComplete, 
  onAuthError,
  showBackButton = false,
  onBack = null,
  isMultiStage = false,
  existingTestimonyId = null
}) => {
  const [recordingState, setRecordingState] = useState('idle'); // idle, permission, recording, recorded, uploading
  const [error, setError] = useState('');
  const [stream, setStream] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [resumableUpload, setResumableUpload] = useState(null);
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [zoomSupported, setZoomSupported] = useState(false);
  const [tipsCollapsed, setTipsCollapsed] = useState(true);
  const [micLevel, setMicLevel] = useState(0); // 0-1 live audio level during recording
  const [audioDevices, setAudioDevices] = useState([]); // available audioinput devices
  const [selectedAudioDeviceId, setSelectedAudioDeviceId] = useState(null);
  const [showMicPicker, setShowMicPicker] = useState(false);

  const videoPreviewRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const chunksRef = useRef([]);
  const micAnalyserRef = useRef(null);
  const micRafRef = useRef(null);

  // Initialize camera on mount and check for resumable uploads
  useEffect(() => {
    initializeCamera();
    checkForResumableUpload();
    return () => {
      stopCamera();
    };
  }, []);

  // Reset state when moving to a new stage (multi-stage mode)
  useEffect(() => {
    if (isMultiStage) {
      console.log('🔄 Stage changed, resetting recording state');
      setRecordingState('idle');
      setRecordedBlob(null);
      setRecordingTime(0);
      setUploadProgress(0);
      setError('');
    }
  }, [currentStageIndex, isMultiStage]);

  // Check if there's an incomplete upload
  const checkForResumableUpload = async () => {
    try {
      // We need to create testimony first to get the ID
      // For now, just check if there's any saved upload state
      const savedState = localStorage.getItem('foh_upload_state');
      if (savedState) {
        const state = JSON.parse(savedState);
        const ageInHours = (Date.now() - state.timestamp) / (1000 * 60 * 60);
        
        if (ageInHours < 24) {
          setResumableUpload(state);
          setShowResumeDialog(true);
        } else {
          // Clear expired state
          clearSavedUploadState();
        }
      }
    } catch (error) {
      console.warn('Failed to check for resumable upload:', error);
    }
  };

  const handleResumeUpload = () => {
    setShowResumeDialog(false);
    // User will need to record video again, but upload will resume from where it left off
    setError('Please record your video again. The upload will resume from where it stopped.');
  };

  const handleDiscardResume = () => {
    setShowResumeDialog(false);
    clearSavedUploadState();
    setResumableUpload(null);
  };

  // Ensure video preview plays when stream is available
  useEffect(() => {
    if (stream && videoPreviewRef.current) {
      const video = videoPreviewRef.current;
      
      // Only update srcObject if it's different and we're not showing a recorded video
      if (video.srcObject !== stream && recordingState !== 'recorded') {
        console.log('🎥 Setting video srcObject to stream');
        video.srcObject = stream;
        video.muted = true; // Ensure muted for autoplay
        
        // Force play
        video.play().catch(err => {
          console.warn('⚠️ Video autoplay failed:', err);
        });
      }
    }
  }, [stream, recordingState]);

  // Recording timer
  useEffect(() => {
    if (recordingState === 'recording') {
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          // Auto-stop at max duration
          if (newTime >= config.video.maxDuration) {
            stopRecording();
          }
          return newTime;
        });
      }, 1000);
    } else {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, [recordingState]);

  const initializeCamera = async () => {
    try {
      setRecordingState('permission');
      setError('');
      
      console.log('🎥 Initializing camera...');
      const mediaStream = await requestCameraPermission();
      console.log('✅ Got media stream:', mediaStream.getTracks().map(t => `${t.kind}: ${t.label}`));
      
      setStream(mediaStream);
      setRecordingState('idle');

      // Enumerate audio input devices (labels only available after permission)
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(d => d.kind === 'audioinput');
        setAudioDevices(audioInputs);
        // Pre-select the currently active audio track's device
        const activeAudioTrack = mediaStream.getAudioTracks()[0];
        if (activeAudioTrack) {
          setSelectedAudioDeviceId(activeAudioTrack.getSettings().deviceId || null);
        }
      } catch (_) {}

      // Check if zoom is supported
      const videoTrack = mediaStream.getVideoTracks()[0];
      if (videoTrack) {
        const capabilities = videoTrack.getCapabilities();
        if (capabilities.zoom) {
          setZoomSupported(true);
          console.log('📷 Zoom supported:', capabilities.zoom);
        }
      }
      
      // Wait a tick for state to update, then set video source
      setTimeout(() => {
        if (videoPreviewRef.current && mediaStream) {
          const video = videoPreviewRef.current;
          console.log('🎥 Setting video srcObject...');
          video.srcObject = mediaStream;
          video.muted = true;
          
          video.play().then(() => {
            console.log('✅ Video playing successfully');
          }).catch(playErr => {
            console.warn('⚠️ Video play error:', playErr);
            // Try playing again with user interaction
            video.onclick = () => {
              video.play();
              video.onclick = null;
            };
          });
        }
      }, 100);
      
    } catch (err) {
      console.error('❌ Camera initialization failed:', err);
      setError(err.message);
      setRecordingState('idle');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stopMediaStream(stream);
    }
  };

  const switchAudioDevice = async (deviceId) => {
    if (!stream) return;
    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: deviceId } }
      });
      const newTrack = audioStream.getAudioTracks()[0];
      // Swap audio tracks in the existing stream
      stream.getAudioTracks().forEach(t => { stream.removeTrack(t); t.stop(); });
      stream.addTrack(newTrack);
      setSelectedAudioDeviceId(deviceId);
      setShowMicPicker(false);
    } catch (err) {
      setError('Could not switch microphone: ' + err.message);
    }
  };

  const handleZoomIn = async () => {
    if (!stream || !zoomSupported) return;
    
    const videoTrack = stream.getVideoTracks()[0];
    const capabilities = videoTrack.getCapabilities();
    const settings = videoTrack.getSettings();
    
    if (capabilities.zoom) {
      const maxZoom = capabilities.zoom.max || 3;
      const step = capabilities.zoom.step || 0.1;
      const newZoom = Math.min(settings.zoom + step, maxZoom);
      
      try {
        await videoTrack.applyConstraints({ advanced: [{ zoom: newZoom }] });
        setZoomLevel(newZoom);
        console.log('🔍 Zoom level:', newZoom);
      } catch (err) {
        console.warn('⚠️ Zoom error:', err);
      }
    }
  };

  const handleZoomOut = async () => {
    if (!stream || !zoomSupported) return;
    
    const videoTrack = stream.getVideoTracks()[0];
    const capabilities = videoTrack.getCapabilities();
    const settings = videoTrack.getSettings();
    
    if (capabilities.zoom) {
      const minZoom = capabilities.zoom.min || 1;
      const step = capabilities.zoom.step || 0.1;
      const newZoom = Math.max(settings.zoom - step, minZoom);
      
      try {
        await videoTrack.applyConstraints({ advanced: [{ zoom: newZoom }] });
        setZoomLevel(newZoom);
        console.log('🔍 Zoom level:', newZoom);
      } catch (err) {
        console.warn('⚠️ Zoom error:', err);
      }
    }
  };
  const startRecording = () => {
    try {
      if (!stream) {
        throw new Error('Camera not initialized');
      }

      chunksRef.current = [];
      setRecordingTime(0);

      // Try different mimeTypes for better compatibility and ensure audio is included
      let mimeType = 'video/webm;codecs=vp9,opus'; // VP9 video + Opus audio
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm;codecs=vp8,opus'; // VP8 video + Opus audio
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'video/webm'; // Fallback to default
        }
      }

      const recorder = new MediaRecorder(stream, {
        mimeType: mimeType,
        audioBitsPerSecond: 128000, // 128 kbps for audio
        videoBitsPerSecond: 2500000  // 2.5 Mbps for video
      });

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        // Stop mic level monitor
        if (micRafRef.current) cancelAnimationFrame(micRafRef.current);
        if (micAnalyserRef.current) {
          try { micAnalyserRef.current.audioCtx.close(); } catch (_) {}
          micAnalyserRef.current = null;
        }
        setMicLevel(0);

        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        setRecordedBlob(blob);
        setRecordingState('recorded');
        
        // Show recorded video in preview
        if (videoPreviewRef.current) {
          videoPreviewRef.current.srcObject = null;
          videoPreviewRef.current.src = URL.createObjectURL(blob);
          videoPreviewRef.current.controls = true;
        }
      };

      recorder.start(1000); // Collect data every second
      setMediaRecorder(recorder);
      setRecordingState('recording');
      setError('');

      // Start live mic level monitor
      try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        micAnalyserRef.current = { analyser, audioCtx };
        const buf = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          analyser.getByteFrequencyData(buf);
          const avg = buf.reduce((s, v) => s + v, 0) / buf.length;
          setMicLevel(avg / 255);
          micRafRef.current = requestAnimationFrame(tick);
        };
        micRafRef.current = requestAnimationFrame(tick);
      } catch (_) { /* analyser is best-effort */ }
    } catch (err) {
      setError('Failed to start recording: ' + err.message);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
  };

  const retakeVideo = () => {
    setRecordedBlob(null);
    setRecordingTime(0);
    setRecordingState('idle');
    setError('');
    
    // Clear chunks from previous recording
    chunksRef.current = [];
    
    // Restore camera preview
    if (videoPreviewRef.current && stream) {
      videoPreviewRef.current.srcObject = stream;
      videoPreviewRef.current.src = '';
      videoPreviewRef.current.controls = false;
    }
  };

  const uploadVideo = async () => {
    if (!recordedBlob) {
      setError('No video recorded');
      return;
    }

    try {
      setRecordingState('uploading');
      setError('');
      setUploadProgress(0);

      // Check file size
      if (recordedBlob.size > config.video.maxFileSize) {
        throw new Error('Video file is too large. Please record a shorter video.');
      }

      // Check audio presence — block silent / muted recordings
      try {
        const { hasAudio, rms } = await checkAudioPresence(recordedBlob);
        console.log('🔊 Audio check — RMS:', rms.toFixed(5), '| hasAudio:', hasAudio);
        if (!hasAudio) {
          setRecordingState('recorded');
          throw new Error('No audio detected in your video. Please check your microphone and re-record.');
        }
      } catch (audioErr) {
        if (audioErr.message.includes('No audio detected')) throw audioErr;
        // If the check itself fails (unsupported browser etc.), log and continue
        console.warn('⚠️ Audio presence check failed, skipping:', audioErr.message);
      }

      // Use recorded duration from timer (more reliable than blob metadata)
      // MediaRecorder often doesn't embed duration in webm metadata
      let duration = recordingTime;
      console.log('✅ Using recorded duration from timer:', duration, 'seconds');
      
      // Fallback: try to extract from blob if timer is 0 (edge case)
      if (!duration || duration === 0) {
        try {
          console.log('⚠️ Timer duration is 0, attempting blob extraction. Blob size:', recordedBlob.size, 'type:', recordedBlob.type);
          duration = await getVideoDuration(recordedBlob);
          console.log('✅ Blob duration extracted:', duration, 'seconds');
        } catch (durationError) {
          console.warn('⚠️ Failed to extract duration from blob:', durationError.message);
          // If both methods fail, set to null
          duration = null;
        }
      }
      
      console.log('📝 Step 1: Creating testimony...');
      setUploadProgress(10);
      
      // Step 1: Create testimony record or use existing one
      let testimonyId;
      let videoFileName;
      
      if (existingTestimonyId) {
        // Use existing testimony ID for subsequent stages
        console.log('✅ Using existing testimony ID:', existingTestimonyId);
        testimonyId = existingTestimonyId;
        videoFileName = `testimony-${testimonyId}-${Date.now()}.webm`;
      } else {
        // Create new testimony for first stage
        const testimonyResponse = await testimonyApi.createTestimony(formData, userToken);
        if (!testimonyResponse.success) {
          throw new Error(testimonyResponse.message || 'Failed to create testimony');
        }
        
        testimonyId = testimonyResponse.data.id;
        videoFileName = testimonyResponse.data.videoFileName;
        console.log('✅ Testimony created:', testimonyId);
      }
      
      setUploadProgress(20);
      console.log('📤 Step 2: Uploading video in chunks...');

      // Step 2: Upload video to GCS using chunked upload (with auto-resume capability)
      const videoUrl = await uploadToGCS(
        recordedBlob,
        videoFileName,
        testimonyId,
        (percentage) => {
          // Map upload progress to 20-90%
          const mappedProgress = 20 + (percentage * 0.7);
          setUploadProgress(Math.round(mappedProgress));
        },
        resumableUpload, // Pass resumable state if available
        userToken        // Pass auth token for protected chunk endpoints
      );
      
      console.log('✅ Video uploaded:', videoUrl);
      setUploadProgress(95);

      // Validate videoUrl
      if (!videoUrl) {
        throw new Error('Upload succeeded but no video URL was returned');
      }

      // Clear resumable state after successful upload
      if (resumableUpload) {
        setResumableUpload(null);
      }

      // Prepare upload data
      const uploadData = {
        testimonyId,
        videoUrl,
        duration,
        isMultiStage, // Add flag to indicate multi-stage mode
        metadataJson: {
          resolution: 'auto',
          codec: 'vp9',
          uploadedFrom: 'web',
          userAgent: navigator.userAgent,
          fileSize: recordedBlob.size,
          recordedAt: new Date().toISOString(),
          duration: duration,
          uploadMethod: 'chunked'
        }
      };

      // Step 3: Notify backend
      if (isMultiStage && currentStage) {
        // Multi-stage: notify with stage info
        console.log('📊 Notifying backend about stage upload:', {
          stage: currentStage.stage,
          stageName: currentStage.stageName,
          stageTitle: currentStage.stageTitle
        });
        
        const stageData = {
          stage: currentStage.stage,
          stageName: currentStage.stageName,
          stageTitle: currentStage.stageTitle,
          videoUrl,
          duration,
          transcription: null,
          metadataJson: uploadData.metadataJson
        };
        
        await testimonyApi.notifyStageUpload(testimonyId, stageData);
      } else {
        // Single video: use legacy upload notification
        console.log('📊 Notifying backend about single video upload');
        await testimonyApi.notifyUpload(testimonyId, uploadData);
      }
      
      onUploadComplete(uploadData);
      
      setUploadProgress(100);
    } catch (err) {
      console.error('Upload error:', err);
      
      const friendlyError = parseErrorMessage(err);
      
      // Check if it's an authentication error
      if (err.message && (
        err.message.includes('Authentication required') ||
        err.message.includes('Not authorized') ||
        err.message.includes('token failed') ||
        err.message.includes('session has expired')
      )) {
        setError('Your session has expired. Please start over.');
        // Notify parent to go back to step 1
        if (onAuthError) {
          setTimeout(() => {
            onAuthError();
          }, 2000); // Give user time to see the error message
        }
      } else {
        setError(friendlyError + ' You can resume the upload later.');
      }
      
      setRecordingState('recorded');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Permission state
  if (recordingState === 'permission') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <CameraLoading />
        </div>
      </div>
    );
  }

  // Uploading state
  if (recordingState === 'uploading') {
    return (
      <div className={`${styles.container} ${styles['container-uploading']}`}>
        <div className={`${styles.card} ${styles['card-uploading']}`}>
          <UploadProgress progress={uploadProgress} />
          <h2 className={recordStyles['upload-title']}>UPLOADING YOUR VIDEO</h2>
          <div className={recordStyles['progress-bar']}>
            <div 
              className={recordStyles['progress-fill']} 
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          <p className={recordStyles['upload-warning']}>Please don't close this page</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {recordingState === 'recording' ? (
          <>
            <div className={recordStyles['desktop-recording-header']}>
              <PageTitle 
                title="REC"
                variant="recording"
                subtitle={
                  <>
                    Your video is being recorded<br />
                    Recording time: {formatTime(recordingTime)}
                  </>
                }
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="23 7 16 12 23 17 23 7"/>
                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                  </svg>
                }
              />
            </div>
            {/* Mobile Recording Header */}
            <div className={recordStyles['recording-header-mobile']}>
              <div className={recordStyles['rec-indicator']}>
                <svg className={recordStyles['rec-icon']} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 7l-7 5 7 5V7z"/>
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                </svg>
                <div className={recordStyles['rec-text']}>REC</div>
              </div>
              <div className={recordStyles['recording-time-display']}>
                <p className={recordStyles['recording-time-label']}>Recording time:</p>
                <p className={recordStyles['recording-time-value']}>{formatTime(recordingTime)}</p>
              </div>
            </div>
          </>
        ) : recordingState === 'recorded' ? (
          <PageTitle 
            title="Review Your Video"
            subtitle="Watch your video and submit when ready, or retake if needed."
          />
        ) : (
          <div className={recordStyles['record-header']}>
            {/* Top divider shape - upward chevron */}
            <div className={recordStyles['header-divider-top']}>
              <svg viewBox="0 0 1200 60" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                <polygon points="0,60 600,0 1200,60" fill="#0A3880" />
              </svg>
            </div>
            
            <div className={recordStyles['header-icon']}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 7l-7 5 7 5V7z"/>
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
              </svg>
            </div>
            <h1 className={recordStyles['record-title']}>
              {isMultiStage && currentStage ? currentStage.stageTitle.toUpperCase() : 'RECORD YOUR TESTIMONY'}
            </h1>
            <p className={recordStyles['record-subtitle']}>
              {isMultiStage && currentStage 
                ? currentStage.description || 'Share your story with Folds of Honor. Speak clearly and from the heart.'
                : 'Share your story with Folds of Honor. Speak clearly and from the heart.'}
            </p>
          </div>
        )}

        {error && (
          <div className={`${styles.alert} ${styles['alert-error']}`}>
            {error}
            {error.includes('camera') && (
              <button 
                className={recordStyles['retry-button']}
                onClick={initializeCamera}
              >
                Try Again
              </button>
            )}
          </div>
        )}

        {/* Video Preview */}
        <div className={`${recordStyles['video-container']} ${
          recordingState === 'recording' || recordingState === 'recorded' 
            ? recordStyles['video-container-no-overlap'] 
            : ''
        }`}>
          {recordingState === 'permission' && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              color: 'white',
              zIndex: 10
            }}>
              <LoadingSpinner />
              <p style={{ marginTop: '1rem' }}>Initializing camera...</p>
            </div>
          )}
          
          {!stream && recordingState === 'idle' && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              color: 'white',
              zIndex: 10,
              padding: '20px'
            }}>
              <p style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>Camera Not Ready</p>
              <button 
                onClick={initializeCamera}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#16A34A',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Enable Camera
              </button>
            </div>
          )}
          
          <video
            ref={videoPreviewRef}
            className={recordStyles.video}
            autoPlay={recordingState !== 'recorded'}
            playsInline
            muted={recordingState !== 'recorded'}
            style={{ visibility: stream && recordingState !== 'permission' ? 'visible' : 'hidden' }}
          />

          {recordingState === 'recorded' && (
            <div className={recordStyles['duration-badge']}>
              {formatTime(recordingTime)}
            </div>
          )}

          {/* Live mic level indicator — shown only during recording */}
          {recordingState === 'recording' && (
            <div className={recordStyles['mic-indicator']} title="Microphone level">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
                <path d="M12 1a4 4 0 0 1 4 4v7a4 4 0 0 1-8 0V5a4 4 0 0 1 4-4z"/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                <line x1="12" y1="19" x2="12" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <line x1="8" y1="23" x2="16" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <div className={recordStyles['mic-bars']}>
                {[0.15, 0.35, 0.55, 0.75, 1.0].map((threshold, i) => (
                  <div
                    key={i}
                    className={recordStyles['mic-bar']}
                    style={{
                      backgroundColor: micLevel >= threshold
                        ? (micLevel < 0.2 ? '#facc15' : '#22c55e')
                        : 'rgba(255,255,255,0.25)',
                      height: `${40 + i * 12}%`,
                    }}
                  />
                ))}
              </div>
              {micLevel < 0.05 && audioDevices.length > 1 && (
                <button
                  className={recordStyles['mic-switch-btn']}
                  onClick={() => { stopRecording(); setShowMicPicker(true); }}
                  title="Switch microphone"
                >
                  Switch mic
                </button>
              )}
              {micLevel < 0.05 && audioDevices.length <= 1 && (
                <span className={recordStyles['mic-warning']}>No mic</span>
              )}
            </div>
          )}

          {/* Mic device picker — shown in idle or after stopping due to no audio */}
          {showMicPicker && audioDevices.length > 1 && recordingState !== 'recording' && (
            <div className={recordStyles['mic-picker']}>
              <p className={recordStyles['mic-picker-title']}>Choose a microphone:</p>
              {audioDevices.map(device => (
                <button
                  key={device.deviceId}
                  className={`${recordStyles['mic-picker-option']} ${device.deviceId === selectedAudioDeviceId ? recordStyles['mic-picker-active'] : ''}`}
                  onClick={() => switchAudioDevice(device.deviceId)}
                >
                  {device.label || `Microphone ${audioDevices.indexOf(device) + 1}`}
                </button>
              ))}
              <button
                className={recordStyles['mic-picker-cancel']}
                onClick={() => setShowMicPicker(false)}
              >
                Cancel
              </button>
            </div>
          )}

          {/* Zoom Controls - Only show on mobile when zoom is supported and not recording/recorded */}
          {isMobileDevice() && zoomSupported && recordingState !== 'recorded' && recordingState !== 'uploading' && (
            <div className={recordStyles['zoom-controls']}>
              <button 
                onClick={handleZoomOut}
                className={recordStyles['zoom-button']}
                disabled={recordingState === 'recording'}
                aria-label="Zoom out"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="M21 21l-4.35-4.35"></path>
                  <line x1="8" y1="11" x2="14" y2="11"></line>
                </svg>
              </button>
              <button 
                onClick={handleZoomIn}
                className={recordStyles['zoom-button']}
                disabled={recordingState === 'recording'}
                aria-label="Zoom in"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="M21 21l-4.35-4.35"></path>
                  <line x1="11" y1="8" x2="11" y2="14"></line>
                  <line x1="8" y1="11" x2="14" y2="11"></line>
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Mic selector — shown in idle when multiple audio devices are available */}
        {recordingState === 'idle' && audioDevices.length > 1 && (
          <div className={recordStyles['mic-select-row']}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M12 1a4 4 0 0 1 4 4v7a4 4 0 0 1-8 0V5a4 4 0 0 1 4-4z"/>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
              <line x1="12" y1="19" x2="12" y2="23"/>
              <line x1="8" y1="23" x2="16" y2="23"/>
            </svg>
            <select
              className={recordStyles['mic-select']}
              value={selectedAudioDeviceId || ''}
              onChange={e => switchAudioDevice(e.target.value)}
            >
              {audioDevices.map((device, i) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Microphone ${i + 1}`}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Tips / Questions (show before and during recording) */}
        {(recordingState === 'idle' || recordingState === 'recording') && (
          <div className={recordStyles['tips-container']}>
            <button
              className={recordStyles['tips-toggle']}
              onClick={() => setTipsCollapsed(prev => !prev)}
              aria-expanded={!tipsCollapsed}
            >
              <h3 className={recordStyles['tips-title']}>
                {currentStage?.prompts ? 'What to include in this video' : 'Recording tips'}
              </h3>
              <svg
                className={`${recordStyles['tips-chevron']} ${tipsCollapsed ? '' : recordStyles['tips-chevron-open']}`}
                width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            <ul className={`${recordStyles['tips-list']} ${tipsCollapsed ? recordStyles['tips-list-collapsed'] : ''}`}>
              {currentStage?.prompts ? (
                currentStage.prompts.map((prompt, index) => (
                  <li key={index} className={recordStyles['tip-item']}>
                    <span className={recordStyles['tip-checkmark']}>✓</span>
                    <span>{prompt}</span>
                  </li>
                ))
              ) : (
                <>
                  <li className={recordStyles['tip-item']}>
                    <span className={recordStyles['tip-checkmark']}>✓</span>
                    <span>Look at the camera and speak clearly</span>
                  </li>
                  <li className={recordStyles['tip-item']}>
                    <span className={recordStyles['tip-checkmark']}>✓</span>
                    <span>Find a quiet location with good lighting</span>
                  </li>
                  <li className={recordStyles['tip-item']}>
                    <span className={recordStyles['tip-checkmark']}>✓</span>
                    <span>Keep your testimony under 2 minutes</span>
                  </li>
                  <li className={recordStyles['tip-item']}>
                    <span className={recordStyles['tip-checkmark']}>✓</span>
                    <span>Share what Folds of Honor means to You</span>
                  </li>
                </>
              )}
            </ul>
          </div>
        )}

        {/* Progress Header - Below Tips */}
        {isMultiStage && allStages.length > 0 && recordingState !== 'recording' && (
          <div className={recordStyles.progressHeader}>
            <div className={recordStyles.progressBar} style={{ height: '4px', marginBottom: '12px' }}>
              <div className={recordStyles.progressFill} style={{ width: `${progress}%` }} />
            </div>
            <div className={recordStyles.stagesIndicator}>
              {allStages.map((stage, index) => (
                <div
                  key={stage.stage}
                  className={`${recordStyles.stageIndicator} ${
                    completedStages.includes(stage.stage) ? recordStyles.completed :
                    index === currentStageIndex ? recordStyles.active :
                    recordStyles.pending
                  }`}
                >
                  <div className={recordStyles.stageNumber}>
                    {completedStages.includes(stage.stage) ? '✓' : stage.stage}
                  </div>
                  <div className={recordStyles.stageLabel}>{stage.stageTitle}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Controls */}
        <div className={recordStyles.controls}>
          {showBackButton && onBack && recordingState === 'idle' && (
            <button
              className={`${styles.button} ${styles['button-secondary']}`}
              onClick={onBack}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
              Back
            </button>
          )}
          
          {recordingState === 'idle' && (
            <button
              className={`${styles.button} ${recordStyles['record-button']}`}
              onClick={startRecording}
              disabled={!stream}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                <circle cx="5" cy="5" r="5"/>
              </svg>
              Start Recording
            </button>
          )}

          {recordingState === 'recording' && (
            <button
              className={`${styles.button} ${recordStyles['stop-button']}`}
              onClick={stopRecording}
            >
              <span className={recordStyles['button-icon']}>■</span>
              Stop Recording
            </button>
          )}

          {recordingState === 'recorded' && (
            <>
              <button
                className={`${styles.button} ${styles['button-secondary']}`}
                onClick={retakeVideo}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                </svg>
                Retake Video
              </button>
              <button
                className={styles.button}
                onClick={uploadVideo}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Submit Video
              </button>
            </>
          )}
        </div>

        {/* Max duration warning */}
        {recordingState === 'recording' && recordingTime >= config.video.maxDuration - 10 && (
          <div className={`${styles.alert} ${styles['alert-info']}`}>
            Recording will automatically stop at {formatTime(config.video.maxDuration)}.
          </div>
        )}

        {/* Resume Upload Dialog */}
        {showResumeDialog && resumableUpload && (
          <div className={resumeStyles['resume-dialog-overlay']}>
            <div className={resumeStyles['resume-dialog']}>
              <h3>📤 Resume Previous Upload?</h3>
              <p>
                We found an incomplete upload from {new Date(resumableUpload.timestamp).toLocaleDateString()} at {new Date(resumableUpload.timestamp).toLocaleTimeString()}.
              </p>
              <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                <strong>⚠️ Important:</strong> Click "START FRESH" to upload all chunks again.
              </p>
              <div className={resumeStyles['resume-stats']}>
                <p><strong>Progress:</strong> Chunk {resumableUpload.lastCompletedChunk + 1} of {resumableUpload.totalChunks}</p>
                <p><strong>File Size:</strong> {(resumableUpload.videoSize / (1024 * 1024)).toFixed(2)} MB</p>
              </div>
              <div className={resumeStyles['resume-buttons']}>
                <button
                  className={`${styles.button} ${styles['button-secondary']}`}
                  onClick={handleDiscardResume}
                >
                  START FRESH
                </button>
                <button
                  className={styles.button}
                  onClick={handleResumeUpload}
                >
                  RESUME UPLOAD
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecordStep;
